import Data, { treeNode } from '@/data'
import { InventorySlotType } from '@/data/enums'
import Store, { defaultPlanner, StoreState } from '@/store'
import { AtlasEmbed, PassivesEmbed, SkillsEmbedSkill, TreeVariant } from '@/types/embed'
import { Gem } from '@/types/item'
import { ItemId, PlayerClass, Profile } from '@/types/profile'
import { format } from '@/utils/format'
import { notNull } from '@/utils/validation'
import { getUniqueId } from '@maxroll-media-group/common-libs'
import { gemName, validateGem } from '../gem'
import { itemParseText, itemUpdateStats, parseBase } from '../item'
import {
  atlasNodeLimit,
  lightState,
  passiveNodeLimit,
  treeCreateHistory,
  TreeEditingState,
  TreeProperties,
  treeViewState
} from '../tree'
import { TreeDataProvider } from '../treeData'

function convertSlot(slot: string, x?: number): InventorySlotType | undefined {
  switch (slot) {
    case 'Amulet':
      return 'Amulet'
    case 'Belt':
      return 'Belt'
    case 'BodyArmour':
      return 'BodyArmour'
    case 'Boots':
      return 'Boots'
    case 'Gloves':
      return 'Gloves'
    case 'Helm':
      return 'Helm'
    case 'Ring':
      return 'Ring'
    case 'Ring2':
      return 'Ring2'
    case 'Flask':
      switch (x) {
        case 0:
          return 'Flask1'
        case 1:
          return 'Flask2'
        case 2:
          return 'Flask3'
        case 3:
          return 'Flask4'
        case 4:
          return 'Flask5'
      }
      break
    case 'Weapon':
      return 'Weapon'
    case 'Offhand':
      return 'Offhand'
    case 'Weapon2':
      return 'Weapon2'
    case 'Offhand2':
      return 'Offhand2'
  }
}

function importTree(
  planner: Pick<Profile, 'version' | 'class' | 'ascendancy'>,
  passives: PassivesData,
  jewels: Record<string, ItemId>,
  items: StoreState['items']
) {
  const props: TreeProperties = {
    version: planner.version,
    charClass: planner.class,
    ascendancy: planner.ascendancy,
    limit: passiveNodeLimit
  }

  const variant: TreeVariant = {
    history: [],
    jewels: {},
    masteries: {},
    name: 'Imported Tree',
    notes: [],
    tattoos: {}
  }

  const state: TreeEditingState = {
    history: [],
    position: 0,
    masteries: variant.masteries,
    jewels: variant.jewels,
    tattoos: {}
  }

  const nodes = passives.hashes.slice()
  const expNodes = new Set(passives.hashes_ex?.map(String) ?? [])

  while (Object.values(jewels).some(Boolean)) {
    const light = lightState(state)
    state.history = nodes.slice()
    state.position = state.history.length
    const view = treeViewState(props, light, items)
    const data = new TreeDataProvider(view, items)
    const added: number[] = []
    for (const id of data.allNodes()) {
      if (variant.jewels[id]) continue
      const node = data.node(id)
      if (!node) continue
      const slot = Data.jewel_slots[node.skill_id]
      if (slot == null) continue
      const jewel = jewels[slot.index]
      if (!jewel) continue
      variant.jewels[id] = jewel
      delete jewels[slot.index]
      added.push(slot.index)
    }
    if (!added.length) break

    const view2 = treeViewState(props, light, items)
    const data2 = new TreeDataProvider(view2, items)

    for (const slot of added) {
      const subgraph = passives.jewel_data[slot]?.subgraph
      if (!subgraph) continue
      for (const group of Object.values(subgraph.groups)) {
        const proxyNode = data2.node(Number(group.proxy))
        if (!proxyNode) continue
        const gnodes = group.nodes
          .filter((id) => subgraph.nodes[id]?.orbit)
          .sort((a, b) => subgraph.nodes[a].orbitIndex - subgraph.nodes[b].orbitIndex)
        const start = gnodes.findIndex((id) => subgraph.nodes[id].in.some((id) => !subgraph.nodes[id]))
        if (start < 0) continue
        if (start) gnodes.push(...gnodes.splice(0, start))
        const refNodes = proxyNode.group.nodes
        gnodes.forEach((id, index) => {
          if (expNodes.has(id) && refNodes[index]) {
            nodes.push(refNodes[index])
          }
        })
      }
    }
  }

  for (const mastery of Object.entries(passives.mastery_effects ?? {})) {
    const masteryData = mastery.map((id) => (typeof id === 'number' ? id : parseInt(id as string)))
    const node = treeNode(masteryData[0], planner.version)
    if (!node?.mastery?.effectsNodeIds) continue
    const index = node.mastery.effectsNodeIds.indexOf(masteryData[1])
    variant.masteries[masteryData[0]] = index
  }

  variant.history = treeCreateHistory(props, state, nodes, items)
  return variant
}

function importAtlasTree(planner: Pick<Profile, 'version'>, hashes: number[]) {
  const props: TreeProperties = {
    version: planner.version,
    atlas: true,
    league: true,
    limit: atlasNodeLimit
  }

  const variant: TreeVariant = {
    history: [],
    jewels: {},
    masteries: {},
    name: 'Imported Tree',
    notes: [],
    tattoos: {}
  }

  const state: TreeEditingState = {
    history: [],
    position: 0,
    masteries: variant.masteries,
    jewels: variant.jewels,
    tattoos: {}
  }

  variant.history = treeCreateHistory(props, state, hashes, {})
  return variant
}

function asNumber(str: string | null) {
  if (!str) return
  const num = Number(str)
  return isNaN(num) ? undefined : num
}
function asNumberArray(str: string | null) {
  const result: number[] = []
  if (!str) return result
  for (const p of str.split(',')) {
    const id = Number(p)
    if (!isNaN(id)) result.push(id)
  }
  return result
}

function getAnyValue(elem: Element) {
  const numberAttr = elem.getAttribute('number')
  if (numberAttr !== null) return parseFloat(numberAttr)

  const strAttr = elem.getAttribute('string')
  if (strAttr !== null) return strAttr

  const boolAttr = elem.getAttribute('boolean')
  if (boolAttr !== null) return boolAttr === 'true' || boolAttr === 'True'

  return null
}

type GggRealm = string
export type GggItem = {
  realm: GggRealm
  verified: boolean
  w: number
  h: number
  icon: string
  league: string
  id: string
  sockets?: { group: number; type: string; item?: string }[]
  name: string
  typeLine: string
  baseType: string
  rarity?: string
  ilvl: number
  identified: boolean
  corrupted?: boolean
  properties?: {
    name: string
    values: (string | number)[][]
    displayMode: number
    type?: number
  }[]
  requirements?: {
    name: string
    values: (string | number)[][]
    displayMode: number
    type?: number
  }[]
  grantedSkills?: {
    name: string
    values: (string | number)[][]
    displayMode: number
    icon: string
  }[]
  runeMods?: string[]
  enchantMods?: string[]
  utilityMods?: string[]
  implicitMods?: string[]
  explicitMods?: string[]
  craftedMods?: string[]
  descrText?: string
  flavourText?: string[]
  frameType: number
  x?: number
  y?: number
  inventoryId?: string
  socket?: number
  socketedItems?: GggItem[]
}

type GggSkill = {
  realm: GggRealm
  verified: boolean
  w: number
  h: number
  icon: string
  support: boolean
  league: string
  id: string
  gemSockets?: string[]
  sockets?: { group: number; type: string }[]
  name: string
  typeLine: string
  baseType: string
  ilvl: number
  identified: boolean
  corrupted?: boolean
  properties?: {
    name: string
    values: (string | number)[][]
    displayMode: number
    type?: number
  }[]
  requirements?: {
    name: string
    values: (string | number)[][]
    displayMode: number
    type?: number
  }[]
  descrText: string
  frameType: number
  x?: number
  y?: number
  inventoryId?: string
  socketedItems?: GggSkill[]
  socket?: number
}

type JewelData = {
  type: string
  radius?: number
  radiusVisual?: string
  subgraph?: {
    groups: Record<
      string,
      {
        nodes: string[]
        orbits: number[]
        proxy: string
        x: number
        y: number
      }
    >
    nodes: Record<
      string,
      {
        expansionJewel?: {
          size: number
          index: number
          proxy: string
          parent: string
        }
        group: string
        icon: string
        in: string[]
        isJewelSocket: boolean
        name: string
        orbit: number
        orbitIndex: number
        out: string[]
        skill: string
        stats: string[]
      }
    >
  }
}

type PassivesData = {
  hashes: number[]
  hashes_ex?: number[]
  specialisations: { set1?: number[]; set2?: number[] }
  skill_overrides: Record<
    string,
    {
      name: string
      icon: string
      grantedStrength?: number
      grantedDexterity?: number
      grantedIntelligence?: number
      stats: string[]
    }
  >
  jewel_data: Record<string, JewelData>
  bandit_choice?: string
  pantheon_major?: string
  pantheon_minor?: string
  mastery_effects?: Record<number, number>
}

export type GggCharacter = {
  id: string
  name: string
  realm: string
  class: string // 'Witch1'
  league: string
  level: number
  experience: number
  equipment: GggItem[]
  skills: GggSkill[]
  inventory: any[]
  jewels: GggItem[]
  passives: PassivesData
  metadata: { version: string }
}

export type GggLeagueAccount = {
  atlas_passive_trees: {
    name: string
    hashes: number[]
  }[]
}

export function getClassAndAsc(charClass: string) {
  if (Data.ascendancyClass[charClass]) {
    return { charClass: Data.ascendancyClass[charClass], ascendancy: charClass }
  }

  for (const [baseKey, base] of Object.entries(Data.characters)) {
    if (base.name === charClass) return { charClass: baseKey as PlayerClass, ascendancy: undefined }
  }

  return { charClass: undefined, ascendancy: undefined }
}

export function parseGggItem(item: GggItem) {
  const textContent = [
    `Rarity: ${item.rarity}`,
    item.name,
    item.baseType,
    `Item Level: ${item.ilvl}`,
    ...(item.properties ?? [])
      .map((property) => {
        if (!property.name.includes('Quality')) return
        if (!property.values.length) return
        if (property.displayMode === 3) {
          return format(property.name, ...property.values.map((v) => v[0]))
        } else {
          return `${property.name}: ${property.values[0][0]}`
        }
      })
      .filter(notNull),
    ...(item.grantedSkills ?? []).map((mod) => `Grants Skill: ${mod.values[0][0]}`),
    // ...(item.utilityMods || []).map((mod) => `${stripMarkup(mod)}`),
    ...(item.enchantMods || []).map((mod) => `${mod} (enchant)`),
    ...(item.implicitMods || []).map((mod) => `${mod} (implicit)`),
    ...(item.explicitMods || []).map((mod) => `${mod}`),
    ...(item.craftedMods || []).map((mod) => `${mod} (crafted)`),
    ...(item.corrupted ? ['Corrupted'] : [])
  ].join('\n')
  const data = itemParseText(textContent)
  if (data && item.socketedItems) {
    for (const socket of item.socketedItems) {
      const base = parseBase(socket.baseType)
      if (base) data.sockets.push(base)
    }
  }
  return data
}

export function parseGggCharacter(
  character: GggCharacter,
  existingState?: StoreState,
  leagueAccount?: GggLeagueAccount
): StoreState {
  const { charClass, ascendancy } = getClassAndAsc(character.class)

  if (!charClass) throw Error(`Invalid class ${character.class}`)

  const planner = existingState?.planner ? { ...existingState.planner } : defaultPlanner(charClass)
  planner.version = Data.defaultVersion
  planner.equipment = { ...planner.equipment }
  planner.equipment.variants = planner.equipment.variants.filter((v) => v && Object.values(v.items).some(Boolean))
  planner.skills = { ...planner.skills }
  planner.skills.steps = planner.skills.steps.filter((s) => s && s.skills.length)
  planner.passives = { ...planner.passives }
  planner.passives.variants = planner.passives.variants.filter((v) => v && v.history.length)
  planner.atlas = { ...planner.atlas }
  planner.atlas.variants = planner.atlas.variants.filter((v) => v && v.history.length)

  planner.class = charClass
  planner.ascendancy = ascendancy

  const items: StoreState['items'] = existingState?.items ? { ...existingState.items } : {}

  const level = character.level
  if (level && !isNaN(Number(level))) planner.level = Number(level)

  function addItem(item: GggItem) {
    const data = parseGggItem(item)

    if (data) {
      const id = Object.keys(items).pop()
      const newIndex = id ? Number(id) + 1 : 1
      items[newIndex] = itemUpdateStats(data)
      return newIndex
    }
  }

  const equipmentVariant: {
    id: string
    items: {
      [key in InventorySlotType]?: ItemId
    }
  } = {
    id: getUniqueId(),
    items: {}
  }
  const skills: SkillsEmbedSkill[] = []

  const gemMap: Record<string, string> = {}
  for (const [id, gem] of Object.entries(Data.gems)) {
    if (validateGem(gem, id)) {
      gemMap[gemName(gem)] = id
    }
  }

  for (const slot of character.equipment) {
    if (!slot.inventoryId) continue
    const pos = convertSlot(slot.inventoryId, slot.x)
    if (!pos) continue
    const id = addItem(slot)
    if (id !== undefined) equipmentVariant.items[pos] = id

    if (slot.socketedItems && slot.sockets) {
      const groups: Gem[][] = []
      slot.socketedItems.forEach((item, index) => {
        const group = slot.sockets![index]?.group
        if (group == null) return
        const id = gemMap[item.baseType]
        if (!id) return
        const gem: Gem = {
          id,
          level: 1,
          quality: 0,
          corrupted: item.corrupted
        }
        if (item.properties) {
          for (const p of item.properties) {
            const stringValue = p?.values?.[0]?.[0]
            if (typeof stringValue !== 'string') continue
            const value = parseInt(stringValue)
            if (isNaN(value)) continue
            if (p.name === 'Level') gem.level = value
            else if (p.name === 'Quality') gem.quality = value
          }
        }

        if (!groups[group]) groups[group] = []
        groups[group].push(gem)
      })
      for (const group of groups) {
        if (!group) continue
        skills.push({
          slot: pos,
          gems: group
        })
      }
    }
  }

  planner.equipment.variants.push(equipmentVariant)
  planner.equipment.active = planner.equipment.variants.length - 1
  planner.skills.steps.push({ name: '', skills })
  planner.skills.step = planner.skills.steps.length - 1

  const jewels: Record<number, ItemId> = {}

  for (const item of character.jewels) {
    if (item.x == null) continue
    const itemId = addItem(item)
    if (itemId === undefined) continue
    jewels[item.x] = itemId
  }

  // for (const [id, node] of Object.entries(Data.passive_tree.nodes)) {
  //   if (node.skill_id === skillId) {
  //     variant.jewels[id as any] = itemId
  //     break
  //   }
  // }

  // const attrib = character.passives.skill_overrides
  // for (const [nodeIdString, entry] of Object.entries(attrib)) {
  //   const nodeId = asNumber(nodeIdString)
  //   const attrVal = attrMap[entry.name]
  //   if (nodeId && attrVal !== undefined) {
  //     variant.attributes[nodeId] = attrVal
  //   }
  // }

  const variant = importTree(planner, character.passives, jewels, items)
  variant.name = `Tree ${planner.passives.variants.length + 1}`
  planner.passives.variants.push(variant)
  planner.passives.active = planner.passives.variants.length - 1

  if (leagueAccount?.atlas_passive_trees.length) {
    planner.atlas.active = planner.atlas.variants.length
    for (const { name, hashes } of leagueAccount.atlas_passive_trees) {
      const variant = importAtlasTree(planner, hashes)
      variant.name = name
      planner.atlas.variants.push(variant)
    }
  }

  function addPantheon(id: string | undefined) {
    if (!id) return
    const data = Data.pantheon[id]
    if (data) planner.pantheon[id] = data.effects.map(() => true)
  }
  planner.pantheon = {}
  addPantheon(character.passives.pantheon_major)
  addPantheon(character.passives.pantheon_minor)

  if (character.passives.bandit_choice) {
    planner.bandits = character.passives.bandit_choice.toLowerCase()
  }

  // const skills: SkillsEmbedSkill[] = []
  // for (const skill of character.skills) {
  //   const gems: Gem[] = []
  //   for (const gem of [skill, ...(skill.socketedItems ?? [])]) {
  //     if (gem.baseType === 'Punch' || gem.baseType === 'Removed Skill') continue
  //     const gemId = Object.entries(Data.items).find(
  //       ([_key, item]) =>
  //         item.name === gem.baseType && gem.support == item.tags.includes('support_gem') && item.skill_gem
  //     )?.[0]

  //     if (!gemId || !Data.items[gemId]) continue
  //     const level = asNumber((gem.properties?.find((prop) => prop.name === 'Level')?.values[0][0] as string) || '0')
  //     const quality = asNumber(
  //       (gem.properties?.find((prop) => prop.name === '[Quality]')?.values[0][0] as string) || '0'
  //     )
  //     const item = gemCreate(gemId)
  //     if (level) item.level = level
  //     if (quality) item.quality = quality
  //     if (gem?.corrupted) item.corrupted = true
  //     gems.push(item)
  //   }

  //   if (gems.length > 0) skills.push({ gems })
  // }

  // planner.skills.steps.push({ name: `Set ${planner.skills.steps.length + 1}`, skills })
  // planner.skills.step = planner.skills.steps.length - 1

  if (planner.atlas.variants.length === 0) {
    planner.atlas.active = 0
    planner.atlas.variants.push({
      name: 'Variant 1',
      history: [],
      masteries: {},
      jewels: {},
      tattoos: {},
      notes: []
    })
  }

  if (planner.passives.variants.length === 0) {
    planner.passives.active = 0
    planner.passives.variants.push({
      name: 'Variant 1',
      history: [],
      masteries: {},
      jewels: {},
      tattoos: {},
      notes: []
    })
  }

  const profileState: StoreState = {
    id: existingState?.id ? existingState.id : Store.newId(),
    embeds: existingState?.embeds ?? [],
    activeEmbed: existingState?.activeEmbed ?? -1,
    items,
    planner
  }

  return profileState
}

export function parseGggPassives(character: GggCharacter): StoreState {
  const { charClass, ascendancy } = getClassAndAsc(character.class)

  if (!charClass) throw Error(`Invalid class ${character.class}`)

  const items: StoreState['items'] = {}

  function addItem(item: GggItem) {
    const data = parseGggItem(item)

    if (data) {
      const id = Object.keys(items).pop()
      const newIndex = id ? Number(id) + 1 : 1
      items[newIndex] = itemUpdateStats(data)
      return newIndex
    }
  }

  const jewels: Record<number, ItemId> = {}

  for (const item of character.jewels) {
    if (item.x == null) continue
    const itemId = addItem(item)
    if (itemId === undefined) continue
    jewels[item.x] = itemId
  }

  const embed: PassivesEmbed = {
    id: 0,
    name: 'Passive Tree',
    type: 'passives',
    charClass,
    ascendancy,
    version: Data.defaultVersion,
    variants: [],
    active: 0
  }

  const variant = importTree(
    {
      version: embed.version,
      class: embed.charClass,
      ascendancy: embed.ascendancy
    },
    character.passives,
    jewels,
    items
  )
  variant.name = `Passive Tree`
  embed.variants.push(variant)

  const profileState: StoreState = {
    id: Store.newId(),
    embeds: [],
    activeEmbed: -1,
    items,
    passive_tree: embed
  }

  return profileState
}

export function parseGggAtlas(league: GggLeagueAccount): StoreState {
  const embed: AtlasEmbed = {
    id: 0,
    name: 'Atlas Tree',
    type: 'atlas',
    version: Data.defaultVersion,
    variants: [],
    active: 0
  }

  for (const { name, hashes } of league.atlas_passive_trees) {
    const variant = importAtlasTree(embed, hashes)
    variant.name = name
    embed.variants.push(variant)
  }

  const profileState: StoreState = {
    id: Store.newId(),
    embeds: [],
    activeEmbed: -1,
    items: {},
    passive_tree: embed
  }

  return profileState
}
